import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import Editor from 'react-simple-code-editor';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { highlight, languages } from 'prismjs';
// Import base languages first
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-c';
// Import dependent languages
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript'; // depends on javascript
import 'prismjs/components/prism-java'; // depends on clike
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-go'; // depends on clike
import 'prismjs/components/prism-rust';
import 'prismjs/components/prism-cpp'; // depends on c
import 'prismjs/components/prism-csharp'; // depends on clike
import 'prismjs/components/prism-php'; // depends on markup-templating
import 'prismjs/components/prism-ruby';
import type { CodeQuestion as CodeType } from '@/types/question-types';
import styles from './CodeQuestion.module.scss';

interface CodeQuestionProps {
  question: CodeType;
  userAnswer?: string;
  onAnswer: (answer: string) => void;
  showExplanation?: boolean;
  isReviewMode?: boolean;
}

export function CodeQuestion({
  question,
  userAnswer,
  onAnswer,
  showExplanation = false,
  isReviewMode = false,
}: CodeQuestionProps) {
  const [code, setCode] = useState(
    userAnswer || question.content.starterCode || ''
  );

  // Language mapping for Prism
  const languageMap: Record<string, any> = {
    python: languages.python,
    javascript: languages.javascript,
    typescript: languages.typescript,
    java: languages.java,
    sql: languages.sql,
    go: languages.go,
    rust: languages.rust,
    cpp: languages.cpp,
    csharp: languages.csharp,
    php: languages.php,
    ruby: languages.ruby,
    other: languages.javascript, // Fallback
  };

  // Update code when userAnswer prop changes
  useEffect(() => {
    if (userAnswer !== undefined && userAnswer !== code) {
      setCode(userAnswer);
    }
  }, [userAnswer]);

  const handleCodeChange = (value: string) => {
    setCode(value);
    onAnswer(value);
  };

  const getLanguageLabel = (lang: string): string => {
    const labels: Record<string, string> = {
      python: 'Python',
      javascript: 'JavaScript',
      typescript: 'TypeScript',
      java: 'Java',
      sql: 'SQL',
      go: 'Go',
      rust: 'Rust',
      cpp: 'C++',
      csharp: 'C#',
      php: 'PHP',
      ruby: 'Ruby',
      other: 'Code',
    };
    return labels[lang] || lang;
  };

  return (
    <div className={styles.codeQuestion}>
      <div className={styles.codeQuestion__header}>
        <h3 className={styles.codeQuestion__questionText}>
          {question.question_text}
        </h3>
        <div className={styles.codeQuestion__languageBadge}>
          {getLanguageLabel(question.content.language)}
        </div>
      </div>

      {!isReviewMode && (
        <div className={styles.codeQuestion__editor}>
          <Editor
            value={code}
            onValueChange={handleCodeChange}
            highlight={(code) =>
              highlight(
                code,
                languageMap[question.content.language] || languages.javascript,
                question.content.language
              )
            }
            padding={16}
            className={styles.codeQuestion__editorField}
            style={{
              fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
              fontSize: 14,
              lineHeight: 1.6,
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              borderRadius: '8px',
              minHeight: '200px',
            }}
            textareaClassName={styles.codeQuestion__textarea}
            placeholder={`Enter your ${getLanguageLabel(question.content.language)} code here...`}
          />
        </div>
      )}

      {isReviewMode && userAnswer && (
        <div className={styles.codeQuestion__reviewAnswer}>
          <div className={styles.codeQuestion__reviewLabel}>Your answer:</div>
          <div className={styles.codeQuestion__reviewContent}>
            <SyntaxHighlighter
              language={question.content.language}
              style={vscDarkPlus}
              customStyle={{
                margin: 0,
                borderRadius: '8px',
                fontSize: '14px',
              }}
            >
              {userAnswer}
            </SyntaxHighlighter>
          </div>
        </div>
      )}

      {showExplanation && (
        <motion.div
          className={styles.codeQuestion__explanation}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.codeQuestion__explanationHeader}>
            <strong>Explanation:</strong>
          </div>
          <p className={styles.codeQuestion__explanationText}>
            {question.explanation}
          </p>
          {question.content.acceptedAnswers.length > 0 && (
            <div className={styles.codeQuestion__correctAnswers}>
              <div className={styles.codeQuestion__correctLabel}>
                Accepted solution{question.content.acceptedAnswers.length > 1 ? 's' : ''}:
              </div>
              {question.content.acceptedAnswers.map((answer, index) => (
                <div
                  key={index}
                  className={styles.codeQuestion__correctAnswer}
                >
                  <SyntaxHighlighter
                    language={question.content.language}
                    style={vscDarkPlus}
                    customStyle={{
                      margin: 0,
                      borderRadius: '8px',
                      fontSize: '14px',
                    }}
                  >
                    {answer}
                  </SyntaxHighlighter>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
